import { z } from "zod";
import { StableValue } from "../../lib/stable-value";
import { safeParse } from "../../lib/zod";
import {
  findBreakpointOverride,
  mergeOrCoalesceFallbacks
} from "../../breakpoints";
import { responsiveValue } from "../../common/schema";
import {
  ControlDefinition,
  serialize
} from "../definition";
import { StyleV2Control } from "./style-v2-control";
class Definition extends ControlDefinition {
  static type = "makeswift::controls::style-v2";
  static get baseSchema() {
    const type = z.literal(Definition.type);
    const resolvedValue = z.string();
    return {
      type,
      resolvedValue
    };
  }
  static schema({ typeDef }) {
    const config = z.object({
      type: typeDef,
      getStyle: z.function().args(z.any()).returns(z.any())
    });
    const { type, resolvedValue } = Definition.baseSchema;
    const definition = z.object({
      type,
      config
    });
    return {
      definition,
      type,
      config,
      resolvedValue
    };
  }
  get typeDef() {
    return this.config.type;
  }
  get controlType() {
    return Definition.type;
  }
  get schema() {
    const typeDef = this.typeDef.schema;
    const data = responsiveValue(typeDef.data);
    const value = responsiveValue(typeDef.value);
    return {
      ...Definition.schema({ typeDef: typeDef.definition }),
      data,
      value
    };
  }
  safeParse(data) {
    return safeParse(this.schema.data, data);
  }
  fromData(data) {
    return data?.map((item) => ({
      ...item,
      value: this.typeDef.fromData(item.value)
    }));
  }
  toData(value) {
    return value?.map((item) => ({
      ...item,
      value: this.typeDef.toData(item.value)
    })) ?? [];
  }
  resolveValue(data, resolver, stylesheet, control) {
    const responsiveValues = (data ?? []).map(({ deviceId, value }) => ({
      deviceId,
      value: this.typeDef.resolveValue(
        value,
        resolver,
        stylesheet,
        control?.child()
      )
    }));
    const getStyle = (breakpoint) => {
      const values = responsiveValues.map(({ deviceId, value }) => ({
        deviceId,
        value: value.readStable()
      }));
      const breakpointValue = findBreakpointOverride(
        stylesheet.breakpoints(),
        values,
        breakpoint.id,
        mergeOrCoalesceFallbacks
      )?.value;
      return this.config.getStyle(breakpointValue);
    };
    const stableValue = StableValue({
      name: Definition.type,
      read: () => stylesheet.defineStyle({ getStyle }),
      deps: responsiveValues.map(({ value }) => value)
    });
    return {
      ...stableValue,
      triggerResolve: async () => await Promise.all(
        responsiveValues.map(({ value }) => value.triggerResolve())
      )
    };
  }
  copyData(data, context) {
    if (data == null)
      return data;
    return data.map((item) => ({
      ...item,
      value: this.typeDef.copyData(item.value, context)
    }));
  }
  createInstance(sendMessage) {
    return new StyleV2Control(this.typeDef, sendMessage);
  }
  serialize() {
    return serialize(this.config, {
      type: Definition.type
    });
  }
  introspect(data, target) {
    if (data == null)
      return [];
    return data.flatMap((item) => this.typeDef.introspect(item.value, target));
  }
}
export {
  Definition as StyleV2Definition
};
//# sourceMappingURL=style-v2.js.map